import { useApolloClient } from '@apollo/client/react/compiled'
import { onWsReconnect } from '@dolpheen/apollo/wsEvents'
import { CaseReminderDueDocument } from '@gql'
import { useSubscription } from '@hooks/graphql'
import useNavigator from '@hooks/useNavigator'
import useNotifier from '@hooks/useNotifier'
import useUser from '@hooks/useUser'
import { useReminderTimeFormat } from '@sections/cases/hooks/useReminderTimeFormat'
import type { ReminderDueAlert, ReminderDueEvent } from '@sections/cases/utils/reminderDueAlert'
import { buildReminderDueAlert } from '@sections/cases/utils/reminderDueAlert'
import { hasOwnReminders } from '@sections/cases/utils/reminderScopes'
import {
  canFireNotification,
  startNotificationCoordinator,
} from '@sections/messaging/store/notificationLeader'
import { useCallback, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'

/** Events arriving within this window become one alert (one tick claims many rows). */
export const REMINDER_DUE_BUFFER_MS = 2_000
/** How long the toast stays: longer than a normal toast, it asks for a call. */
const TOAST_MS = 15_000
const RESTART_BASE_MS = 2_000
const RESTART_MAX_MS = 30_000

/** The queries a due reminder changes: the Home queue and the nav badge. */
export const REMINDER_DUE_REFETCH = ['HomeReminders', 'CaseReminderSummary']

/**
 * The app-wide "reminder due" feed. crm-core's sweep pushes `caseReminderDue`
 * to the case's assignee up to five minutes before the due time. Each burst
 * becomes one alert:
 *
 * - a toast with an "Open case" button in a tab that is on screen;
 * - an OS notification from the elected leader tab while no tab of the app is
 *   on screen (the same election as the unread feed, so ten tabs alert once).
 *
 * It also refetches the Home queue and the badge, on every event and after a
 * ws reconnect (nothing replays across an outage; the badge poll is the
 * fallback).
 */
export function useReminderDueFeed(): void {
  const intl = useIntl()
  const { user } = useUser()
  const client = useApolloClient()
  const navigate = useNavigator()
  const notify = useNotifier()
  const formatTime = useReminderTimeFormat()
  const isEnabled = hasOwnReminders(user)

  // Effect-synced so a timer or a notification click (long after this render)
  // sees the live identities.
  const latest = useRef({ client, formatTime, intl, navigate, notify })
  useEffect(() => {
    latest.current = { client, formatTime, intl, navigate, notify }
  })

  const bufferRef = useRef<ReminderDueEvent[]>([])
  const flushTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const showAlert = useCallback((alert: ReminderDueAlert) => {
    const { navigate, notify } = latest.current
    const open = () => navigate(alert.url)

    if (
      typeof Notification !== 'undefined' &&
      Notification.permission === 'granted' &&
      canFireNotification()
    ) {
      const notification = new Notification(alert.title, { body: alert.body, tag: alert.tag })
      notification.onclick = () => {
        window.focus()
        open()
        notification.close()
      }
    }

    if (!document.hidden) {
      notify({
        actionBtn: { action: open, label: alert.actionLabel },
        autohide: TOAST_MS,
        status: 'info',
        text: alert.body,
        title: alert.title,
      })
    }
  }, [])

  const flush = useCallback(() => {
    flushTimer.current = undefined
    const events = bufferRef.current
    bufferRef.current = []
    const { client, formatTime, intl } = latest.current

    void client.refetchQueries({ include: REMINDER_DUE_REFETCH }).catch(() => undefined)
    const alert = buildReminderDueAlert(events, intl, formatTime)
    if (alert) {
      showAlert(alert)
    }
  }, [showAlert])

  // A failed subscribe completes the operation for good; restart it with a
  // capped backoff, like the messaging feeds do.
  const attemptsRef = useRef(0)
  const restartTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const { restart } = useSubscription(CaseReminderDueDocument, {
    onData: ({ data }) => {
      attemptsRef.current = 0
      const event = data.data?.caseReminderDue
      if (!event) {
        return
      }

      bufferRef.current.push(event)
      flushTimer.current ??= setTimeout(flush, REMINDER_DUE_BUFFER_MS)
    },
    onError: (error) => {
      const attempt = attemptsRef.current++
      const delay = Math.min(RESTART_BASE_MS * 2 ** attempt, RESTART_MAX_MS)
      console.error(
        `caseReminderDue subscription errored (attempt ${attempt + 1}); restarting in ${delay}ms`,
        error,
      )
      clearTimeout(restartTimer.current)
      restartTimer.current = setTimeout(() => restart(), delay)
    },
    skip: !isEnabled,
  })

  useEffect(() => {
    if (!isEnabled) {
      return
    }

    // Ask once, lazily; 'denied' is final and 'granted' is silent.
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      void Notification.requestPermission()
    }

    const stopCoordinator = startNotificationCoordinator()
    const unsubscribe = onWsReconnect(() => {
      void latest.current.client
        .refetchQueries({ include: REMINDER_DUE_REFETCH })
        .catch(() => undefined)
    })
    return () => {
      unsubscribe()
      stopCoordinator()
      clearTimeout(flushTimer.current)
      clearTimeout(restartTimer.current)
      flushTimer.current = undefined
      bufferRef.current = []
    }
  }, [isEnabled])
}

export default useReminderDueFeed
